import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, CodeField, DataTable, Dialog, EmptyState, Inline, PasswordInput, Stack, t, Text, TextField, toast } from '@kanbios/ui';
import { changeRef, PullRequestNotice } from './catalogue/PullRequestNotice';
import type { maintenance } from '@kanbios/api-client';
import { api, ApiFailure, unwrap } from '../lib/api';
import { relativeTime } from '../lib/format';
import { generateSecret, sealForGithub } from '../lib/seal';
import { ImportAction, parseEnvImport, planImport } from '../lib/envImport';

type Variables = maintenance.operations['listInstallationVariables']['responses']['200']['content']['application/json'];
type Variable = Variables['items'][number];

/**
 * SPEC-034 § 7 (lot 1.5, slice 5): the variables deploy/env.example declares.
 * A secret is written without the portal seeing it: sealed in the browser with
 * the GitHub environment's public key (§ 7.2). A change reaches the server at
 * the next deployment, or by « Redéployer la version en place » (§ 7.4).
 */
const secretsUnreadable = (data: Variables | undefined) => Boolean(data && !data.secretsReadable);

export function EnvironmentVariables({ installation, production, canWrite, canRedeploy, onStepUp, importing, onImportClose }: {
  installation: string; production: boolean; canWrite: boolean; canRedeploy: boolean; onStepUp: () => void;
  /** D95: « Importer des variables », the tab's action in the page's tab bar (DSM Tabs). */
  importing: boolean; onImportClose: () => void;
}) {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['environment-variables', installation],
    queryFn: async () => unwrap(await api.maintenance.GET('/installations/{id}/variables', { params: { path: { id: installation } } })) as Variables,
  });
  const [editing, setEditing] = useState<Variable | null>(null);
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const [redeploying, setRedeploying] = useState(false);
  const [plain, setPlain] = useState<Variable | null>(null);
  const [plainValue, setPlainValue] = useState('');
  // § 7, D95: several variables pasted at once (JSON or .env); each one is then changed on its own.
  const [importText, setImportText] = useState('');

  // § 7.1: a non-secret value goes to the catalogue; empty, it goes back to env.example's.
  const savePlain = async () => {
    if (!plain) return;
    setSending(true);
    try {
      const { pullRequest } = unwrap(await api.maintenance.PUT('/installations/{id}/variables', { params: { path: { id: installation } }, body: { values: { [plain.name]: plainValue } } }));
      toast({ tone: 'pos', title: t('environment.variables.plainSaved', { name: plain.name, ref: changeRef(pullRequest) }) });
      setPlain(null);
      void queryClient.invalidateQueries({ queryKey: ['environment-variables', installation] });
    } catch (err) {
      failed(err, () => setPlain(null));
    } finally {
      setSending(false);
    }
  };

  const failed = (err: unknown, close: () => void) => {
    if (err instanceof ApiFailure && err.body.code === 'MFA_STEP_UP_REQUIRED') { close(); onStepUp(); return; }
    toast({ tone: 'neg', title: err instanceof ApiFailure ? err.body.message : String(err), description: err instanceof ApiFailure ? err.body.hint : undefined });
  };

  const save = async () => {
    if (!editing) return;
    setSending(true);
    try {
      const key = unwrap(await api.maintenance.GET('/installations/{id}/secrets/key', { params: { path: { id: installation } } }));
      const encryptedValue = await sealForGithub(value, key.key);
      unwrap(await api.maintenance.PUT('/installations/{id}/secrets/{name}', { params: { path: { id: installation, name: editing.name } }, body: { encryptedValue, keyId: key.keyId } }));
      toast({ tone: 'pos', title: t('environment.variables.saved', { name: editing.name, environment: key.environment }) });
      setEditing(null);
      setValue('');
      void queryClient.invalidateQueries({ queryKey: ['environment-variables', installation] });
    } catch (err) {
      failed(err, () => setEditing(null));
    } finally {
      setSending(false);
    }
  };

  const parsed = parseEnvImport(importText);
  const plan = planImport(parsed.variables, (list.data?.items ?? []).map(v => ({
    name: v.name, secret: v.secret, build: v.build, immutable: v.immutable, present: v.present, value: v.value ?? null, fromCatalogue: v.fromCatalogue ?? false,
  })));
  const toWrite = plan.filter(p => p.action === 'plain' || p.action === 'secret');
  const runImport = async () => {
    setSending(true);
    let secrets = 0;
    try {
      const plain = plan.filter(p => p.action === 'plain');
      // The non-secret ones: one commit to the catalogue.
      if (plain.length) unwrap(await api.maintenance.PUT('/installations/{id}/variables', { params: { path: { id: installation } }, body: { values: Object.fromEntries(plain.map(p => [p.name, p.value])) } }));
      // The secrets: sealed in this browser, one by one, with the environment's key read once.
      const sealed = plan.filter(p => p.action === 'secret');
      if (sealed.length) {
        const key = unwrap(await api.maintenance.GET('/installations/{id}/secrets/key', { params: { path: { id: installation } } }));
        for (const p of sealed) {
          const encryptedValue = await sealForGithub(p.value, key.key);
          unwrap(await api.maintenance.PUT('/installations/{id}/secrets/{name}', { params: { path: { id: installation, name: p.name } }, body: { encryptedValue, keyId: key.keyId } }));
          secrets += 1;
        }
      }
      toast({ tone: 'pos', title: t('environment.variables.import.done', { plain: plain.length, n: secrets }) });
      onImportClose();
      setImportText('');
    } catch (err) {
      if (secrets) toast({ tone: 'warn', title: t('environment.variables.import.partial', { n: secrets }) });
      failed(err, onImportClose);
    } finally {
      setSending(false);
      void queryClient.invalidateQueries({ queryKey: ['environment-variables', installation] });
    }
  };
  const ACTION_TONES: Record<ImportAction, 'brand' | 'neutral' | 'warn' | 'neg'> = {
    secret: 'brand', plain: 'neutral', unchanged: 'neutral', undeclared: 'warn', build: 'warn', immutable: 'warn', reserved: 'neg', empty: 'warn',
  };

  const redeploy = async () => {
    setSending(true);
    try {
      const r = unwrap(await api.maintenance.POST('/installations/{id}/redeploy', { params: { path: { id: installation } } }));
      toast({ tone: 'info', title: t('environment.variables.redeployStarted', { version: r.version }) });
      setRedeploying(false);
    } catch (err) {
      failed(err, () => setRedeploying(false));
    } finally {
      setSending(false);
    }
  };

  const data = list.data;
  // What a click on a row opens: the secret's form (sealed in the browser), or the catalogue value's; nothing if it cannot change.
  const edit = (r: Variable) => {
    if (!canWrite) return;
    if (r.secret && data?.secretsReadable && !(r.immutable && r.present)) { setValue(''); setEditing(r); }
    else if (!r.secret && !r.build && !(r.immutable && r.fromCatalogue)) { setPlainValue(r.value ?? ''); setPlain(r); }
  };
  const missing = (data?.items ?? []).filter(v => v.secret && v.required && v.present === false);
  return (
    <Stack gap={16}>
      <Text variant="body-sm" tone="mute">{t('environment.variables.lead', { environment: data?.environment ?? '' })}</Text>
      {data?.pullRequest && <PullRequestNotice pullRequest={data.pullRequest} />}
      {data && !data.secretsReadable && <Alert tone="warn" title={t('environment.variables.unreadableTitle')}>{t('environment.variables.unreadableBody')}</Alert>}
      {missing.length > 0 && <Alert tone="warn" title={t('environment.variables.missingTitle', { n: missing.length })}>{missing.map(v => v.name).join(', ')}</Alert>}
      {data?.changedSinceDeploy && (
        <Alert tone="info" title={t('environment.variables.changedTitle')} action={data.canRedeploy && canRedeploy ? t('environment.variables.redeploy') : undefined} onAction={() => setRedeploying(true)}>
          {t('environment.variables.changedBody')}
        </Alert>
      )}
      <DataTable
        label={t('environment.variables.table')}
        rows={data?.items ?? []}
        rowKey="name"
        loading={list.isPending}
        rowHeight="auto"
        toolbar={false}
        empty={<EmptyState framed={false} icon="tune" title={t('environment.variables.empty')} />}
        // DSM DataTable: one click on a row opens its editor (no « Changer » button per row).
        onRowClick={(r: Variable) => edit(r)}
        columns={[
          {
            key: 'name', label: t('environment.variables.name'), grow: true,
            render: (r: Variable) => (
              <Stack gap={4}>
                <Text variant="code">{r.name}</Text>
                {r.description && <Text variant="caption" tone="mute">{r.description}</Text>}
              </Stack>
            ),
          },
          {
            key: 'kind', label: t('environment.variables.kind'),
            render: (r: Variable) => (
              <Inline gap={4}>
                <Badge tone={r.secret ? 'brand' : 'neutral'} size="sm">{t(r.secret ? 'environment.variables.secret' : 'environment.variables.plain')}</Badge>
                {r.required && <Badge tone="neutral" size="sm">{t('environment.variables.required')}</Badge>}
                {r.immutable && <Badge tone="warn" size="sm">{t('environment.variables.immutable')}</Badge>}
                {r.build && <Badge tone="neutral" size="sm">{t('environment.variables.build')}</Badge>}
              </Inline>
            ),
          },
          {
            key: 'state', label: t('environment.variables.state'), wrap: true,
            render: (r: Variable) => (r.secret
              ? r.present === null ? <Text variant="body-sm" tone="mute">{t('environment.variables.unknown')}</Text>
                : r.present
                  ? <Text variant="body-sm">{t('environment.variables.setAt', { when: relativeTime(r.updatedAt) })}</Text>
                  : <Badge tone={r.required ? 'warn' : 'neutral'} size="sm">{t('environment.variables.missing')}</Badge>
              : (
                <Stack gap={4}>
                  <Text variant="code">{r.value || '—'}</Text>
                  <Text variant="caption" tone="mute">{t(r.fromCatalogue ? 'environment.variables.fromCatalogue' : 'environment.variables.fromExample')}</Text>
                </Stack>
              )),
          },
        ]}
      />

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={t('environment.variables.dialogTitle', { name: editing?.name ?? '' })}
        description={t(production ? 'environment.variables.dialogBodyProduction' : 'environment.variables.dialogBody', { environment: data?.environment ?? '' })}
        confirmLabel={t('environment.variables.save')}
        confirmDisabled={sending || !value}
        onConfirm={() => void save()}
      >
        {editing && (
          <Stack gap={12}>
            <PasswordInput id="secret-value" label={editing.name} value={value} autoComplete="new-password" mono
              hint={editing.description ?? undefined} onChange={e => setValue(e.target.value)} />
            {editing.generate && (
              <Inline gap={8}>
                <Button variant="secondary" size="sm" icon="casino" onClick={() => setValue(generateSecret(editing.generate!))}>{t('environment.variables.generate', { n: editing.generate })}</Button>
              </Inline>
            )}
            {editing.immutable && <Alert tone="warn" title={t('environment.variables.immutableTitle')}>{t('environment.variables.immutableBody')}</Alert>}
          </Stack>
        )}
      </Dialog>

      <Dialog
        open={plain !== null}
        onClose={() => setPlain(null)}
        title={t('environment.variables.plainTitle', { name: plain?.name ?? '' })}
        description={t(production ? 'environment.variables.plainBodyProduction' : 'environment.variables.plainBody')}
        confirmLabel={t('environment.variables.plainSave')}
        confirmDisabled={sending || Boolean(plain?.required && !plainValue.trim())}
        onConfirm={() => void savePlain()}
      >
        {plain && (
          <TextField id="plain-value" label={plain.name} value={plainValue} mono hint={plain.description ?? t('environment.variables.plainHint', { example: plain.example || '—' })}
            onChange={e => setPlainValue(e.target.value)} />
        )}
      </Dialog>

      <Dialog
        open={importing && (data?.items.length ?? 0) > 0}
        size="lg"
        onClose={onImportClose}
        title={t('environment.variables.import.title')}
        description={t(production ? 'environment.variables.import.bodyProduction' : 'environment.variables.import.body', { environment: data?.environment ?? '' })}
        confirmLabel={t('environment.variables.import.confirm', { n: toWrite.length })}
        confirmDisabled={sending || toWrite.length === 0 || (secretsUnreadable(data) && plan.some(p => p.action === 'secret'))}
        onConfirm={() => void runImport()}
      >
        <Stack gap={16}>
          {/* DSM CodeField: what is pasted as code (JSON or .env lines); no « Copier », the paste may hold secrets. */}
          <CodeField label={t('environment.variables.import.paste')} value={importText} onChange={(v: string) => setImportText(v)} rows={8}
            language={importText.trimStart().startsWith('{') ? 'JSON' : '.env'} copyable={false}
            placeholder={t('environment.variables.import.placeholder')}
            hint={t('environment.variables.import.hint')}
            error={parsed.errors.includes('json') ? t('environment.variables.import.json') : parsed.errors.length ? t('environment.variables.import.unread', { what: parsed.errors.join(', ') }) : undefined} />
          {plan.length > 0 && (
            <DataTable
              label={t('environment.variables.import.table')}
              rows={plan}
              rowKey="name"
              toolbar={false}
              columns={[
                { key: 'name', label: t('environment.variables.name'), mono: true, grow: true },
                // A secret's value is never shown, even here.
                { key: 'value', label: t('environment.variables.import.value'), render: (r: { action: ImportAction; value: string }) => <Text variant="code" truncate>{r.action === 'secret' || r.action === 'immutable' ? '••••••' : r.value || '—'}</Text> },
                { key: 'action', label: t('environment.variables.import.what'), render: (r: { action: ImportAction }) => <Badge tone={ACTION_TONES[r.action]} size="sm">{t(`environment.variables.import.actions.${r.action}`)}</Badge> },
              ]}
            />
          )}
        </Stack>
      </Dialog>

      <Dialog
        open={redeploying}
        onClose={() => setRedeploying(false)}
        title={t('environment.variables.redeployTitle')}
        description={t('environment.variables.redeployBody')}
        confirmLabel={t('environment.variables.redeploy')}
        confirmDisabled={sending}
        onConfirm={() => void redeploy()}
      />
    </Stack>
  );
}
